import { Send } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { SaveAndResumeLater, type SaveDraftResult } from "../../components/forms/SaveAndResumeLater";
import { buildSubmissionSummary, type SubmissionSummary } from "../../lib/formSubmissionSummary";
import {
  appendStoredDraftFiles,
  buildResumeUrl,
  getCurrentSectionId,
  getResumeDraftId,
  loadFormProgress,
  restoreFormProgress,
  saveFormProgress,
  type DraftFileMeta,
} from "../../lib/formProgress";
import {
  CheckboxField,
  CheckboxGroupField,
  FileField,
  FormSection,
  RadioGroupField,
  RequiredMark,
  SelectField,
  TextareaField,
  TextField,
  ThankYouState,
  TurnstilePlaceholder,
  printSubmittedForm,
} from "./FormPageShared";
import { FormPortalHeader } from "./FormPortalHeader";
import { archiveHubSections, archiveHubSummarySections } from "./formsContent";

type FormErrors = Record<string, string>;

const ARCHIVE_FORM_KEY = "archive-hub";
const ARCHIVE_FORM_PATH = "/archive-hub";
const ARCHIVE_FORM_TITLE = "Archive Hub Form";

const requiredTextFields = ["fullName", "email", "phone", "archiveTitle"] as const;
const requiredRadioFields = ["archiveType", "archivePurpose", "archiveStructure", "existingHubs"] as const;
const requiredCheckboxFields = [
  "accurateConfirmation",
  "contentPermission",
  "serviceUseConfirmation",
  "changeRequestAcknowledgement",
] as const;

const requiredLabels: Record<string, string> = {
  fullName: "Full Name is required.",
  email: "Email Address is required.",
  phone: "Phone Number is required.",
  archiveTitle: "Archive Hub Title is required.",
  archiveType: "Please select an Archive Type.",
  archiveTypeOther: "Please describe your Archive Type.",
  archivePurpose: "Please select the primary purpose of this Archive Hub.",
  archivePurposeOther: "Please explain the primary purpose of this Archive Hub.",
  archiveStructure: "Please select how you would like your Archive organized.",
  existingHubs: "Please tell us if RSVP Hubs or Memory Hubs should be included.",
  existingHubList: "Please list the events, Memory Hubs, or RSVP Hubs you would like included.",
  accurateConfirmation: "Please confirm that the information provided is accurate.",
  contentPermission: "Please confirm that you have permission to submit and use the provided content and files.",
  serviceUseConfirmation: "Please confirm that Kreatethedate may use these details to build your Archive Hub.",
  changeRequestAcknowledgement: "Please confirm that you understand major setup changes may require additional time and/or fees.",
};

const contactOptions = ["Email", "Phone", "Text"];
const archiveTypeOptions = [
  "Personal Archive",
  "Family Archive",
  "Couple Archive",
  "Memorial Archive",
  "Organization Archive",
  "Community Archive",
  "Legacy Archive",
  "Other",
];
const archivePurposeOptions = [
  "Preserve family memories",
  "Preserve event history",
  "Honor a loved one",
  "Document a life journey",
  "Preserve organizational history",
  "Create a family legacy",
  "Other",
];
const archiveContentTypeOptions = [
  "Birthdays",
  "Weddings",
  "Anniversaries",
  "Graduations",
  "Baby Showers",
  "Memorials & Celebrations of Life",
  "Family Reunions",
  "Vacations & Trips",
  "Church Events",
  "Community Events",
  "Business Milestones",
  "Other",
];
const archiveStructureOptions = ["By Year", "By Event", "By Life Stage", "By Family Member", "Let Kreatethedate Recommend"];
const preferenceOptions = ["Yes", "No", "Not sure yet"];
const includedKreatethedateServiceOptions = [
  "Existing RSVP Hub(s)",
  "Existing Memory Hub(s)",
  "Future RSVP Hub(s)",
  "Future Memory Hub(s)",
  "Not Sure Yet",
];
const archiveFeelOptions = ["Elegant", "Family-Focused", "Luxury", "Historical", "Modern", "Emotional", "Spiritual", "Minimal", "Other"];
const futureServicesOptions = [
  "RSVP Hub",
  "Memory Hub",
  "Memory Media Vault",
  "Signature Song",
  "Signature Highlight Reel",
  "Not at this time",
];

export function ArchiveHubFormPage() {
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState<SubmissionSummary | null>(null);
  const [draftId, setDraftId] = useState("");
  const [restoreNotice, setRestoreNotice] = useState("");
  const [restoredFiles, setRestoredFiles] = useState<DraftFileMeta[]>([]);
  const [showArchiveTypeOther, setShowArchiveTypeOther] = useState(false);
  const [showArchivePurposeOther, setShowArchivePurposeOther] = useState(false);
  const [showArchiveContentTypesOther, setShowArchiveContentTypesOther] = useState(false);
  const [showExistingHubList, setShowExistingHubList] = useState(false);
  const [showArchiveFeelOther, setShowArchiveFeelOther] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    document.title = "Archive Hub Form | Kreatethedate";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Complete the Kreatethedate Archive Hub Form with timeline, milestones, memories, media, and legacy goals.",
      );
  }, []);

  useEffect(() => {
    const resumeDraftId = getResumeDraftId();
    if (!resumeDraftId) {
      const targetSectionId = window.location.hash.replace("#", "");
      if (targetSectionId) {
        window.setTimeout(() => {
          document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
        }, 80);
      } else {
        window.scrollTo({ top: 0, left: 0 });
      }
      return;
    }

    const draft = loadFormProgress(ARCHIVE_FORM_KEY, resumeDraftId);
    if (!draft || !formRef.current) {
      setRestoreNotice("We could not find saved progress for this resume link in this browser.");
      return;
    }

    restoreFormProgress(formRef.current, draft);
    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    setRestoreNotice("Your saved progress has been restored in this browser.");
    setShowArchiveTypeOther(draft.fields.archiveType === "Other");
    setShowArchivePurposeOther(draft.fields.archivePurpose === "Other");
    setShowArchiveContentTypesOther(Array.isArray(draft.fields.archiveContentTypes) && draft.fields.archiveContentTypes.includes("Other"));
    setShowExistingHubList(draft.fields.existingHubs === "Yes");
    setShowArchiveFeelOther(Array.isArray(draft.fields.archiveFeel) && draft.fields.archiveFeel.includes("Other"));

    const targetSectionId = window.location.hash.replace("#", "") || draft.lastSectionId || archiveHubSections[0]?.id;
    window.setTimeout(() => {
      if (formRef.current) restoreFormProgress(formRef.current, draft);
      document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
    }, 80);
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set("formType", ARCHIVE_FORM_KEY);
    formData.set("cf-turnstile-response", "turnstile-placeholder");
    if (draftId) await appendStoredDraftFiles(formData, ARCHIVE_FORM_KEY, draftId);

    const nextErrors = validateArchiveHubForm(formData);
    setErrors(nextErrors);

    const firstInvalidName = Object.keys(nextErrors)[0];
    if (firstInvalidName) {
      setSubmitError("Please complete all required fields before submitting your Archive Hub Form.");
      form.querySelector<HTMLElement>(`[name="${firstInvalidName}"]`)?.focus();
      return;
    }

    const summary = buildSubmissionSummary({
      formData,
      formTitle: "Archive Hub Setup Form",
      clientNameField: "fullName",
      eventNameField: "archiveTitle",
      eventNameLabel: "Archive title",
      sections: archiveHubSummarySections,
    });

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/forms/archive-hub", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const message = await response.text();
        if (!(import.meta.env.DEV && response.status === 404)) {
          throw new Error(message || "Your form could not be submitted. Please try again.");
        }
      }

      const contentType = response.headers.get("Content-Type") || "";
      if (response.ok && !contentType.includes("application/json") && !import.meta.env.DEV) {
        throw new Error("The Archive Hub submission endpoint is not configured yet.");
      }

      setSubmittedSummary(summary);
      setSubmitted(true);
      formRef.current?.reset();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Your form could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSaveProgress(): Promise<SaveDraftResult> {
    if (!formRef.current) throw new Error("The form is not ready yet.");

    const lastSectionId = getCurrentSectionId(archiveHubSections.map((section) => section.id));
    const draft = await saveFormProgress({
      form: formRef.current,
      formKey: ARCHIVE_FORM_KEY,
      draftId,
      lastSectionId,
    });
    const resumeLink = buildResumeUrl(ARCHIVE_FORM_PATH, draft.draftId, lastSectionId);

    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    return { draft, resumeLink };
  }

  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <a
        href="#archive-hub-form"
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#050505] focus:px-4 focus:py-3 focus:text-[#FF6A2B]"
      >
        Skip to Archive Hub form
      </a>
      <FormPortalHeader />

      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18">
        <img src="/assets/celebration-table.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,5,5,.94),rgba(5,5,5,.74)_54%,rgba(5,5,5,.48)),linear-gradient(0deg,rgba(5,5,5,.42),rgba(5,5,5,.14))]" />
        <div className="relative mx-auto grid min-h-[46dvh] max-w-7xl items-end gap-8 px-4 pb-10 pt-24 text-[#fff9ef] sm:px-6 lg:grid-cols-[1fr_.72fr] lg:px-8 lg:pb-14">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#FF6A2B]">Archive Hub Form</p>
            <h1 className="mt-5 max-w-4xl text-balance font-display text-5xl font-bold leading-[0.98] sm:text-6xl">
              Let&apos;s Build Your Archive Hub
            </h1>
          </div>
          <p className="max-w-xl border-l-2 border-[#FF6A2B] pl-4 text-sm font-semibold leading-7 text-[#fff9ef]/76 lg:ml-auto">
            Share your timeline, milestones, memories, and legacy goals so we can create a beautiful Archive Hub that
            preserves your journey for years to come.
          </p>
        </div>
      </section>

      {submitted ? (
        <ThankYouState
          message="Your Kreatethedate Archive Hub Form has been received. We will review your details and follow up with next steps."
          onDownloadPdf={() => printSubmittedForm(submittedSummary)}
          onPrint={() => printSubmittedForm(submittedSummary)}
          summary={submittedSummary}
        />
      ) : (
        <section className="px-4 pb-28 pt-10 sm:px-6 lg:px-8 lg:pb-28 lg:pt-14">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[290px_1fr]">
            <aside className="hidden h-fit rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] lg:sticky lg:top-24 lg:block">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">Form sections</p>
              <ol className="mt-4 grid gap-2">
                {archiveHubSections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="focus-ring group grid grid-cols-[34px_1fr] items-center gap-3 rounded-lg px-2 py-2 text-sm font-bold text-[#5c5246] transition hover:bg-[#fff9ef] hover:text-[#050505]"
                    >
                      <span className="grid size-8 place-items-center rounded-full border border-[#FF6A2B]/45 bg-[#050505] text-xs text-[#FF6A2B]">
                        {index + 1}
                      </span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <form id="archive-hub-form" ref={formRef} noValidate onSubmit={handleSubmit} className="grid gap-6">
              <SaveAndResumeLater
                formTitle={ARCHIVE_FORM_TITLE}
                getDefaultEmail={() => {
                  const emailControl = formRef.current?.elements.namedItem("email");
                  return emailControl instanceof HTMLInputElement ? emailControl.value : "";
                }}
                onSaveDraft={handleSaveProgress}
                restoreNotice={restoreNotice}
                savedFiles={restoredFiles}
              />
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Estimated completion time: 10&ndash;15 minutes. You can save your progress and return at any time.
              </p>
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Required fields are marked with <span className="font-black text-[#D94A16]">*</span>.
              </p>
              {submitError ? (
                <p className="rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800" role="alert">
                  {submitError}
                </p>
              ) : null}

              <FormSection id="contact" number="01" title="Client Contact Information" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <TextField name="fullName" label="Full Name" autoComplete="name" required error={errors.fullName} />
                    <TextField name="email" label="Email Address" type="email" autoComplete="email" required error={errors.email} />
                    <TextField name="phone" label="Phone Number" type="tel" autoComplete="tel" required error={errors.phone} />
                    <SelectField name="preferredContactMethod" label="Preferred Contact Method" options={contactOptions} />
                  </div>
                  <div className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                    <div>
                      <h3 className="font-display text-2xl font-bold text-[#050505]">Additional Contact (Optional)</h3>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <TextField name="additionalContactName" label="Name" autoComplete="name" />
                      <TextField name="additionalContactPhone" label="Phone Number" type="tel" autoComplete="tel" />
                      <TextField name="additionalContactEmail" label="Email Address" type="email" autoComplete="email" />
                    </div>
                  </div>
                </div>
              </FormSection>

              <FormSection id="archive-details" number="02" title="Archive Details" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField
                    name="archiveTitle"
                    label="Archive Hub Title"
                    helperText="Examples: Angel's Archive, The Johnson Family Archive, The Smith Legacy Archive, Memories of Mom, Our Journey Together"
                    required
                    error={errors.archiveTitle}
                    className="md:col-span-2"
                  />
                  <RadioGroupField
                    name="archiveType"
                    label="Archive Type"
                    options={archiveTypeOptions}
                    required
                    error={errors.archiveType}
                    className="md:col-span-2"
                    onChange={(value) => setShowArchiveTypeOther(value === "Other")}
                  />
                  {showArchiveTypeOther ? (
                    <TextField name="archiveTypeOther" label="Please describe." required error={errors.archiveTypeOther} className="md:col-span-2" />
                  ) : null}
                </div>
              </FormSection>

              <FormSection id="purpose" number="03" title="Purpose of the Archive" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="archivePurpose"
                    label="What is the primary purpose of this Archive Hub?"
                    options={archivePurposeOptions}
                    required
                    error={errors.archivePurpose}
                    onChange={(value) => setShowArchivePurposeOther(value === "Other")}
                  />
                  {showArchivePurposeOther ? <TextField name="archivePurposeOther" label="Please explain." required error={errors.archivePurposeOther} /> : null}
                  <CheckboxGroupField
                    name="archiveContentTypes"
                    label="What types of memories would you like included in your Archive?"
                    helperText="Select all that apply."
                    options={archiveContentTypeOptions}
                    onChange={(option, checked) => {
                      if (option === "Other") setShowArchiveContentTypesOther(checked);
                    }}
                  />
                  {showArchiveContentTypesOther ? <TextField name="archiveContentTypesOther" label="Please describe." /> : null}
                  <TextareaField
                    name="archiveVision"
                    label="Tell us about the story, purpose, or vision for your Archive Hub."
                    rows={7}
                  />
                </div>
              </FormSection>

              <FormSection id="timeline" number="04" title="Timeline Structure" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="archiveStructure"
                    label="How would you like your Archive organized?"
                    options={archiveStructureOptions}
                    required
                    error={errors.archiveStructure}
                  />
                  <TextareaField
                    name="importantMilestones"
                    label="Are there important dates, years, or milestones that should appear in the Archive?"
                    rows={7}
                  />
                </div>
              </FormSection>

              <FormSection id="existing-memories" number="05" title="Existing Memories & Events" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="existingHubs"
                    label="Do you already have RSVP Hubs or Memory Hubs that should be included in this Archive?"
                    options={preferenceOptions}
                    required
                    error={errors.existingHubs}
                    onChange={(value) => setShowExistingHubList(value === "Yes")}
                  />
                  <CheckboxGroupField
                    name="includedKreatethedateServices"
                    label="Will this Archive Hub include any Kreatethedate services?"
                    helperText="Select all that apply."
                    options={includedKreatethedateServiceOptions}
                  />
                  {showExistingHubList ? (
                    <TextareaField
                      name="existingHubList"
                      label="Please list the events, Memory Hubs, or RSVP Hubs you would like included."
                      required
                      error={errors.existingHubList}
                      rows={7}
                    />
                  ) : null}
                </div>
              </FormSection>

              <FormSection id="future-plans" number="06" title="Future Archive Plans" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="futureEventsOverTime"
                    label="Would you like to continue adding future events to this Archive over time?"
                    options={preferenceOptions}
                  />
                  <TextareaField
                    name="futureArchivePlans"
                    label="Tell us about future events, milestones, or memories you may want added later."
                    rows={7}
                  />
                </div>
              </FormSection>

              <FormSection id="design-preferences" number="07" title="Design Preferences" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4 md:grid-cols-2">
                  <CheckboxGroupField
                    name="archiveFeel"
                    label="How would you like your Archive Hub to feel?"
                    options={archiveFeelOptions}
                    className="md:col-span-2"
                    onChange={(option, checked) => {
                      if (option === "Other") setShowArchiveFeelOther(checked);
                    }}
                  />
                  {showArchiveFeelOther ? <TextField name="archiveFeelOther" label="Please describe." className="md:col-span-2" /> : null}
                  <TextField name="preferredColors" label="Preferred Colors" />
                  <TextareaField name="designAvoid" label="Anything you would like us to avoid?" rows={6} className="md:col-span-2" />
                </div>
              </FormSection>

              <FormSection id="featured-legacy" number="08" title="Featured Memories & Legacy" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <TextareaField
                    name="featuredLegacyMemory"
                    label="Is there a specific person, memory, milestone, achievement, or story that should be highlighted?"
                    rows={7}
                  />
                  <TextareaField
                    name="futureGenerationsMemory"
                    label="What memories, stories, people, or moments would you most like this Archive to preserve?"
                    helperText="Share the people, milestones, traditions, achievements, life moments, or memories that are most important to preserve within your Archive Hub."
                    rows={7}
                  />
                </div>
              </FormSection>

              <FormSection id="media-content" number="09" title="Media & Content" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField name="providingMediaNow" label="Will you be providing photos and videos now?" options={preferenceOptions} />
                  <div className="grid gap-4 md:grid-cols-2">
                    <FileField
                      name="archivePhotos"
                      label="Photos"
                      accept=".jpg,.jpeg,.png,.heic,.heif,.webp,image/jpeg,image/png,image/heic,image/heif,image/webp"
                      multiple
                    />
                    <FileField name="archiveVideos" label="Videos" accept=".mp4,.mov,video/mp4,video/quicktime" multiple />
                    <FileField
                      name="archiveDocuments"
                      label="Documents"
                      accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                      multiple
                    />
                    <FileField name="archivePrograms" label="Programs" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" multiple />
                    <FileField name="archiveFlyers" label="Flyers" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" multiple />
                    <FileField
                      name="archiveHistoricalMaterials"
                      label="Historical Materials"
                      accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.doc,.docx,.txt,application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                      multiple
                    />
                  </div>
                  <TextareaField name="archiveMediaInstructions" label="Additional Media Instructions" rows={7} />
                </div>
              </FormSection>

              <FormSection id="future-services" number="10" title="Future Services Interest" formName={ARCHIVE_FORM_TITLE}>
                <CheckboxGroupField
                  name="futureServicesInterest"
                  label="Would you like information about additional Kreatethedate services?"
                  options={futureServicesOptions}
                />
              </FormSection>

              <FormSection id="confirmation" number="11" title="Permission & Confirmation" formName={ARCHIVE_FORM_TITLE}>
                <div className="grid gap-4">
                  <CheckboxField
                    name="accurateConfirmation"
                    label="I confirm that the information provided is accurate to the best of my knowledge."
                    required
                    error={errors.accurateConfirmation}
                  />
                  <CheckboxField
                    name="contentPermission"
                    label="I confirm that I have permission to submit and use any photos, videos, logos, graphics, wording, links, or files provided for this project."
                    required
                    error={errors.contentPermission}
                  />
                  <CheckboxField
                    name="serviceUseConfirmation"
                    label="I understand Kreatethedate will build my Archive Hub based on the information submitted."
                    required
                    error={errors.serviceUseConfirmation}
                  />
                  <CheckboxField
                    name="changeRequestAcknowledgement"
                    label="I understand that major changes requested after setup has started may require additional time and/or additional fees."
                    required
                    error={errors.changeRequestAcknowledgement}
                  />
                  <TurnstilePlaceholder />
                </div>
              </FormSection>

              <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                <div className="grid gap-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Archive Hub Form</p>
                    <h2 className="mt-1 font-display text-2xl font-bold text-[#050505]">Change Request Notice</h2>
                  </div>
                  <div className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    <p>Please review your information carefully before submitting.</p>
                    <p>
                      Kreatethedate will use the information provided to create your Archive Hub. Major changes requested
                      after setup has begun may affect the project timeline and may require additional work, revisions, or
                      fees.
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex flex-col gap-3 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold leading-7 text-[#5c5246]">
                  Please review your details before submitting your Archive Hub form.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-6 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting..." : "Submit Archive Hub Form"}
                  <Send className="size-4" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}

function validateArchiveHubForm(formData: FormData) {
  const nextErrors: FormErrors = {};

  for (const name of requiredTextFields) {
    const value = String(formData.get(name) || "").trim();
    if (!value) nextErrors[name] = requiredLabels[name];
  }

  for (const name of requiredRadioFields) {
    const value = String(formData.get(name) || "").trim();
    if (!value) nextErrors[name] = requiredLabels[name];
  }

  if (String(formData.get("archiveType") || "").trim() === "Other" && !String(formData.get("archiveTypeOther") || "").trim()) {
    nextErrors.archiveTypeOther = requiredLabels.archiveTypeOther;
  }

  if (String(formData.get("archivePurpose") || "").trim() === "Other" && !String(formData.get("archivePurposeOther") || "").trim()) {
    nextErrors.archivePurposeOther = requiredLabels.archivePurposeOther;
  }

  if (String(formData.get("existingHubs") || "").trim() === "Yes" && !String(formData.get("existingHubList") || "").trim()) {
    nextErrors.existingHubList = requiredLabels.existingHubList;
  }

  const email = String(formData.get("email") || "").trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    nextErrors.email = "Enter a valid email address.";
  }

  for (const name of requiredCheckboxFields) {
    if (!formData.get(name)) nextErrors[name] = requiredLabels[name];
  }

  return nextErrors;
}
